// Browser-side TSpoonLab client + Pack IA report builder.
// Ported from tspoonreport/tspoonreport/reports/packia/report.py.

import axios, { AxiosRequestConfig } from "axios";
import { dateInReportPeriod, isReceivedOrderStatus, orderOperationalDate, type ReportPeriod } from "./reportFilters";

const TSPOON_BASE = (import.meta.env.VITE_TSPOON_BASE_URL || "https://app.tspoonlab.com").replace(/\/+$/, "");

export const tspoon = axios.create({
  baseURL: TSPOON_BASE,
  timeout: 60_000,
  headers: { source: "tSpoonFast" },
});

function jsonHeaders(rememberme: string, order?: string): Record<string, string> {
  const h: Record<string, string> = {
    Accept: "application/json, text/plain, */*",
    "X-Requested-With": "XMLHttpRequest",
    rememberme,
  };
  if (order) h.order = order;
  return h;
}

// ---------- Auth ----------
export interface LoginResponse { rememberme: string; username: string; }

export async function login(username: string, password: string): Promise<LoginResponse> {
  const form = new URLSearchParams();
  form.set("username", username);
  form.set("password", password);
  const res = await tspoon.post("/recipes/api/login", form.toString(), {
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    validateStatus: () => true,
  });
  if (res.status >= 400) throw new Error(`Login failed: HTTP ${res.status}`);

  let token = (res.headers["rememberme"] as string) || "";
  if (!token) {
    const body = res.data;
    if (typeof body === "string") {
      const t = body.trim();
      if (t.startsWith("{") || t.startsWith("[")) {
        try { const o = JSON.parse(t); token = typeof o === "string" ? o : (o?.rememberme || o?.token || ""); }
        catch { /* ignore */ }
      } else if (t && !t.startsWith("<")) token = t;
    } else if (body && typeof body === "object") token = body.rememberme || body.token || "";
  }
  if (!token) throw new Error("Login OK pero sin token rememberme");
  return { username, rememberme: token };
}

// ---------- Cost centers ----------
export interface CenterInfo { id: string; descr: string; currency?: string; }

export async function listCenters(rememberme: string): Promise<CenterInfo[]> {
  const res = await tspoon.get("/recipes/api/orderCenters", { headers: jsonHeaders(rememberme) });
  const rows = Array.isArray(res.data) ? res.data : (res.data?.rows || []);
  return rows.map((r: any) => ({
    id: String(r.idOrderCenter ?? r.id),
    descr: r.descr || r.name || `Centro ${r.idOrderCenter ?? r.id}`,
    currency: r.currency || "EUR",
  }));
}

// ---------- Dependent users + user switch ----------
export interface DependentUser {
  id: string; descr: string; name: string; surname: string; email?: string;
  phone?: string; idClient?: string; module?: string; client?: string;
  listOrderCenters?: any[];
}
export interface DependentUsersPage { rows: DependentUser[]; total: number; }

function normalizeUser(r: any): DependentUser {
  return {
    id: String(r.id ?? r.idUser ?? ""),
    descr: r.descr || "", name: r.name || "", surname: r.surname || "",
    email: r.email || r.mail || undefined,
    phone: r.phone,
    idClient: r.idClient != null ? String(r.idClient) : undefined,
    module: r.module, client: r.client,
    listOrderCenters: r.listOrderCenters || [],
  };
}

export async function listDependentUsers(
  rememberme: string,
  opts: { filter?: string; rows?: number; start?: number; signal?: AbortSignal } = {},
): Promise<DependentUsersPage> {
  const params: Record<string, string | number> = { rows: opts.rows ?? 50, start: opts.start ?? 0 };
  if (opts.filter) params.filter = opts.filter;
  const res = await tspoon.get("/recipes/api/listUsersDependentPaged/", {
    headers: jsonHeaders(rememberme), params, signal: opts.signal,
  });
  const data = res.data;
  const raw = Array.isArray(data) ? data : (data?.rows || data?.data || []);
  return { rows: raw.map(normalizeUser), total: Array.isArray(data) ? raw.length : Number(data?.total ?? raw.length) };
}

export interface SharedLoginResult { activeRememberme: string; originRememberme: string; }

export async function loginShared(rememberme: string, userId: string): Promise<SharedLoginResult> {
  const res = await tspoon.get(`/recipes/api/loginShared/${encodeURIComponent(userId)}`, {
    headers: { "Content-Type": "application/json", rememberme },
    withCredentials: true,
    validateStatus: () => true,
  });
  if (res.status < 200 || res.status >= 300) throw new Error(`loginShared falló: HTTP ${res.status}`);
  const active = (res.headers["rememberme"] as string) || "";
  const back = (res.headers["rememberme-back"] as string) || "";
  if (!active) throw new Error("loginShared OK pero falta la cabecera 'rememberme'");
  return { activeRememberme: active, originRememberme: back };
}

export interface UserInfo {
  id?: string; user?: string; name?: string; surname?: string; email?: string; descr?: string;
  listOrderCenters?: any[];
  listOrderCenter?: any[];
  client?: { logo?: string };
}

export async function getInfo(rememberme: string, order?: string, signal?: AbortSignal): Promise<UserInfo> {
  const res = await tspoon.get("/recipes/api/info", { headers: jsonHeaders(rememberme, order), signal });
  return res.data || {};
}

// ===========================================================================
// Pack IA data sources
// ===========================================================================

export interface PendingOrder { vendor: string; date: string; total: number; currency: string; }
export type IncidentKind = "pending_reception" | "received_with_observation";
export interface Incident { vendor: string; reference: string; observation: string; kind: IncidentKind; date: string; }
export interface NewVendor { id: string; name: string; tax_id: string; }
export interface SheetVendor { nif: string; name: string; }
export type SheetItemKind = "producto" | "herramienta" | "otro";
export interface SheetItem { name: string; kind: SheetItemKind; }
export interface SheetArticle {
  product: string; vendor: string; code: string; format: string; price: string; article: string;
  kind: SheetItemKind;
}
export interface NewProduct { id: string; name: string; type: number; color: number; }
export interface CreatedItem { id: string; name: string; kind: "producto" | "herramienta"; }
export interface NewArticle { product: string; vendor: string; code: string; unit: string; price: number; vendor_descr: string; }
export interface WeeklyBucket { label: string; received: number; not_received: number; total: number; }
export interface PriceVariation { product: string; variation_pct: number; price_max: number; price_min: number; quantity: number; unit: string; }

export interface PackIaData {
  report_type: "packia";
  center: string;
  center_id: string;
  currency: string;
  year: number;
  month: number;
  month_label: string;
  center_logo_url: string;
  invoice_count: number;
  pending_orders_count: number;
  incidents_count: number;
  pending_orders: PendingOrder[];
  incidents: Incident[];
  new_vendors: NewVendor[];
  // Optional data sourced from Google Sheet "C"'s linked tab (sheet mode only).
  sheet_new_vendors?: SheetVendor[];
  sheet_new_items?: SheetItem[];
  sheet_new_articles?: SheetArticle[];
  new_products: NewProduct[];
  created_items: CreatedItem[];
  new_articles: NewArticle[];
  weekly_buckets: WeeklyBucket[];
  total_received: number;
  total_not_received: number;
  total_purchased: number;
  price_variations: PriceVariation[];
  summary: string;
  recommendations: string[];
}

const MONTH_NAMES_ES: Record<number, string> = {
  1: "ENERO", 2: "FEBRERO", 3: "MARZO", 4: "ABRIL",
  5: "MAYO", 6: "JUNIO", 7: "JULIO", 8: "AGOSTO",
  9: "SEPTIEMBRE", 10: "OCTUBRE", 11: "NOVIEMBRE", 12: "DICIEMBRE",
};

function lastDayOfMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

// --- /recipes/api/listOrdersPagedExt (paged) -------------------------------
async function fetchOrdersList(
  rememberme: string, centerId: string, year: number, month: number,
  pageSize = 100, signal?: AbortSignal,
): Promise<any[]> {
  const all: any[] = [];
  let start = 0;
  for (;;) {
    const url = "/recipes/api/listOrdersPagedExt";
    const params = { allOrderCenters: "false", filter: "", rows: pageSize, searchType: 0, start };
    const { data } = await tspoon.get(url, { headers: jsonHeaders(rememberme, centerId), params, signal });
    const batch: any[] = Array.isArray(data) ? data : [];
    all.push(...batch);
    if (batch.length < pageSize) break;
    // Stop early once we drop below the requested year/month boundary.
    const older = batch.some((o) => {
      const oy = o.year, om = o.month;
      return oy != null && om != null && (oy < year || (oy === year && om < month));
    });
    if (older) break;
    start += pageSize;
  }
  return all.filter((o) => o.year === year && o.month === month);
}

// --- /recipes/api/order/{id}/vendor/none -----------------------------------
async function fetchOrderDetail(rememberme: string, centerId: string, orderId: string, signal?: AbortSignal): Promise<any> {
  const { data } = await tspoon.get(`/recipes/api/order/${encodeURIComponent(orderId)}/vendor/none`, {
    headers: jsonHeaders(rememberme, centerId), signal,
  });
  return data;
}

// --- /recipes/api/reportex/orders weekly -----------------------------------
async function fetchWeeklyOrders(
  rememberme: string, centerId: string, year: number, month: number, signal?: AbortSignal,
): Promise<any> {
  const last = lastDayOfMonth(year, month);
  const range = encodeURIComponent(`${year}-${month}-1 ${year}-${month}-${last}`);
  const url = `/recipes/api/reportex/orders/${range}/range/W/all/${centerId}/none/all/none/all/2`;
  const { data } = await tspoon.get(url, { headers: jsonHeaders(rememberme, centerId), signal });
  return data;
}

// --- /recipes/api/reportex/prices ------------------------------------------
async function fetchPrices(
  rememberme: string, centerId: string, year: number, month: number, signal?: AbortSignal,
): Promise<any> {
  const url = `/recipes/api/reportex/prices/${year}/${month}/M/all/${centerId}/none`;
  const { data } = await tspoon.get(url, { headers: jsonHeaders(rememberme, centerId), signal });
  return data;
}

// --- /recipes/api/reportex/products ----------------------------------------
async function fetchProducts(rememberme: string, centerId: string, signal?: AbortSignal): Promise<any> {
  const url = `/recipes/api/reportex/products/all/null/${centerId}/none`;
  const { data } = await tspoon.get(url, { headers: jsonHeaders(rememberme, centerId), signal });
  return data;
}

// --- parsers ---------------------------------------------------------------
function vendorName(comanda: any): string {
  return (comanda?.vendor?.descr) || comanda?.descr || "";
}

function parseWeeklyBuckets(p: any): WeeklyBucket[] {
  return (p?.listCompres || []).map((it: any) => ({
    label: it.descr || "",
    received: round2(Number(it.value) || 0),
    not_received: round2(Number(it.valueNotReceived) || 0),
    total: round2(Number(it.valueTotal) || 0),
  }));
}

function parsePriceVariations(p: any): PriceVariation[] {
  const items = (p?.listPrices || [])
    // type 0 = producto · 1 = herramienta · 2 = otro coste. Only products.
    .filter((it: any) => Number(it.type) === 0)
    .filter((it: any) => Number(it.costMin) > 0)
    .map((it: any) => {
      const min = Number(it.costMin), max = Number(it.costMax);
      const spread = ((max - min) / min) * 100;
      return {
        product: it.descr || "Sin descripcion",
        variation_pct: round2(spread),
        price_max: round2(max),
        price_min: round2(min),
        quantity: Math.round((Number(it.quantity) || 0) * 1000) / 1000,
        unit: it.unit || "",
      };
    })
    .filter((it: PriceVariation) => it.variation_pct > 0);
  items.sort((a: PriceVariation, b: PriceVariation) => b.variation_pct - a.variation_pct);
  return items.slice(0, 15);
}

function parseNewVendors(p: any): NewVendor[] {
  return (p?.listNewVendors || []).map((it: any) => ({
    id: it.id || "",
    name: it.descr || "",
    tax_id: it.cif || it.taxId || "",
  }));
}

function parseNewProducts(p: any): NewProduct[] {
  return (p?.listNewComponents || []).map((it: any) => ({
    id: it.id || "",
    name: it.descr || "",
    type: Number(it.type) || 0,
    color: Number(it.color) || 0,
  }));
}

function parseNewArticles(p: any): NewArticle[] {
  return (p?.listNewFormats || []).map((it: any) => ({
    product: it.component || it.descr || "",
    vendor: it.vendor || "",
    code: it.codi || "",
    unit: it.unit || "",
    price: round2(Number(it.cost) || 0),
    vendor_descr: it.descrVendor || it.vendorDescr || "",
  }));
}

function round2(n: number): number { return Math.round(n * 100) / 100; }

export function processOrderDetails(
  details: Record<string, any>,
  pendingOrderIds: Set<string>,
  period: ReportPeriod,
): { invoice_count: number; pending_orders: PendingOrder[]; incidents: Incident[] } {
  let invoice_count = 0;
  const pending_orders: PendingOrder[] = [];
  const incidents: Incident[] = [];

  for (const [orderId, detail] of Object.entries(details)) {
    for (const c of (detail?.listComandes || [])) {
      const inPeriod = dateInReportPeriod(orderOperationalDate(c), period);
      if (c.boundFile && inPeriod) invoice_count++;
      if (pendingOrderIds.has(orderId) && inPeriod && !isReceivedOrderStatus(c.status)) {
        const dateStr = c.dateReceptionFlat || c.dateGeneratedFlat || "";
        pending_orders.push({
          vendor: vendorName(c),
          date: dateStr,
          total: round2(Number(c.cost) || 0),
          currency: c.currency || "€",
        });
      }
      const observation = String(c.incidencia || "").trim();
      if (observation && inPeriod) {
        // This is the same authoritative status used by Recipes to show its green
        // check. A delivery number or a typed reception date does not change state.
        const received = isReceivedOrderStatus(c.status);
        incidents.push({
          kind: received ? "received_with_observation" : "pending_reception",
          date: c.dateReceptionFlat || c.dateGeneratedFlat || "",
          vendor: vendorName(c),
          reference: c.numAlbara || "",
          observation,
        });
      }
    }
  }
  return { invoice_count, pending_orders, incidents };
}

interface TransactionLogSummary {
  id: string;
  user?: string;
  dateFormatted?: string;
  timeFormatted?: string;
}

interface TransactionLogDetail {
  entityId?: string;
  desc?: string;
  entity?: string;
  type?: string;
  transaction?: { created?: boolean };
}

const SUPPORT_USERNAME_PATTERN = /^soporte_ts(?:p)?@/i;

export function isSupportUsername(value: unknown): value is string {
  return typeof value === "string" && SUPPORT_USERNAME_PATTERN.test(value.trim());
}

async function findSupportUser(rememberme: string, signal?: AbortSignal): Promise<{ id: string; username: string }> {
  // Logs must always be attributed to the technical tSpoonLab user. Do not
  // infer it from the logged-in user: that can select a restaurant user.
  const { data: users } = await tspoon.get("/recipes/api/users/listUsersByClient", {
    headers: jsonHeaders(rememberme),
    params: { filter: "soporte_ts", start: 0, rows: 100 }, signal,
  });
  const candidates = Array.isArray(users) ? users : [];
  const support = candidates.find((user: any) =>
    isSupportUsername(user.user) || isSupportUsername(user.email) || isSupportUsername(user.mail),
  );
  if (!support?.id) {
    throw new Error("No se ha encontrado un usuario de soporte (soporte_ts@ o soporte_tsp@) para consultar Registro de Cambios");
  }
  return {
    id: String(support.id),
    username: [support.user, support.email, support.mail].find(isSupportUsername) || "soporte técnico",
  };
}

async function fetchTransactionLogPage(
  rememberme: string, userId: string, type: "INGREDIENT" | "MATERIAL", period: ReportPeriod, start: number, signal?: AbortSignal,
): Promise<TransactionLogSummary[]> {
  const lastDay = lastDayOfMonth(period.year, period.month);
  const { data } = await tspoon.get("/recipes/api/transactionlogs/search", {
    headers: jsonHeaders(rememberme),
    params: {
      userId, type, start, rows: 100,
      dateFrom: `${period.year}-${String(period.month).padStart(2, "0")}-01`,
      dateTo: `${period.year}-${String(period.month).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`,
    }, signal,
  });
  return Array.isArray(data) ? data : [];
}

async function fetchTransactionLogDetails(
  rememberme: string, summaries: TransactionLogSummary[], signal?: AbortSignal,
): Promise<TransactionLogDetail[]> {
  const details: TransactionLogDetail[] = [];
  const batchSize = 8;
  for (let index = 0; index < summaries.length; index += batchSize) {
    const batch = summaries.slice(index, index + batchSize);
    const results = await Promise.allSettled(batch.map(async ({ id }) => {
      const { data } = await tspoon.get(`/recipes/api/transactionlogs/${encodeURIComponent(id)}/detail`, {
        headers: jsonHeaders(rememberme), signal,
      });
      return data as TransactionLogDetail;
    }));
    const failed = results.find((result) => result.status === "rejected");
    if (failed?.status === "rejected") {
      throw new Error(`No se ha podido leer el detalle de Registro de Cambios: ${failed.reason?.message || failed.reason}`);
    }
    for (const result of results) if (result.status === "fulfilled") details.push(result.value);
  }
  return details;
}

export async function fetchSupportCreatedItems(
  rememberme: string, period: ReportPeriod, signal?: AbortSignal,
): Promise<{ items: CreatedItem[]; username: string }> {
  const support = await findSupportUser(rememberme, signal);
  const summaries: TransactionLogSummary[] = [];

  for (const type of ["INGREDIENT", "MATERIAL"] as const) {
    for (let start = 0; ; start += 100) {
      const page = await fetchTransactionLogPage(rememberme, support.id, type, period, start, signal);
      summaries.push(...page);
      if (page.length < 100) break;
    }
  }

  const details = await fetchTransactionLogDetails(rememberme, summaries, signal);
  const items = details
    .filter((detail) => detail.entity === "Components" && detail.transaction?.created)
    .map((detail) => ({
      id: detail.entityId || "",
      name: detail.desc || "Sin nombre",
      kind: detail.type === "2" ? "herramienta" as const : "producto" as const,
    }))
    .filter((item) => item.id);

  return {
    items: Array.from(new Map(items.map((item) => [item.id, item])).values())
      .sort((a, b) => a.name.localeCompare(b.name, "es")),
    username: support.username,
  };
}

function buildCenterLogoUrl(
  rememberme: string, centerLogoPath?: string, centerPublicOrderId?: string,
): string {
  if (!centerLogoPath || !centerPublicOrderId) return "";
  const path = centerLogoPath.startsWith("/") ? centerLogoPath : `/${centerLogoPath}`;
  const qs = new URLSearchParams({
    rememberme, language: "es", order: centerPublicOrderId, recipe: "all", timezone: "Europe/London",
  });
  return `${TSPOON_BASE}/recipes/api${path}?${qs.toString()}`;
}

function extractCenterLogoInfo(info: UserInfo, selectedCenterId: string): { logoPath?: string; publicOrderId?: string } {
  const logoPath = info?.client?.logo;
  for (const c of (info.listOrderCenter || [])) {
    if (String(c.id) === selectedCenterId || String(c.idOrderCenter) === selectedCenterId) {
      return { logoPath, publicOrderId: String(c.idOrderCenter || c.id) };
    }
  }
  return { logoPath };
}

// --- Public builder --------------------------------------------------------
export interface BuildOptions {
  rememberme: string;
  centerId: string;
  centerName?: string;
  year: number;
  month: number;
  signal?: AbortSignal;
  onLog?: (msg: string, level?: "info" | "call" | "ok" | "err" | "step") => void;
}

export async function buildPackIaReport(opts: BuildOptions): Promise<PackIaData> {
  const { rememberme, centerId, year, month, signal } = opts;
  const log = (level: "info" | "call" | "ok" | "err" | "step", msg: string) =>
    opts.onLog?.(msg, level);

  log("step", `Pack IA · ${MONTH_NAMES_ES[month]} ${year} · centro ${centerId}`);

  // Optional: /info → derive logo path
  let logoPath: string | undefined;
  let publicOrderId: string | undefined;
  try {
    log("call", "GET /recipes/api/info");
    const t0 = performance.now();
    const info = await getInfo(rememberme, centerId, signal);
    const { logoPath: lp, publicOrderId: poid } = extractCenterLogoInfo(info, centerId);
    logoPath = lp; publicOrderId = poid;
    log("ok", `info OK en ${Math.round(performance.now() - t0)} ms`);
  } catch (err: any) {
    log("err", `info no disponible: ${err?.message || err}`);
  }

  // Phase 1: parallel fetch of 4 sources
  log("call", "GET listOrdersPagedExt · reportex/orders (W) · reportex/prices · reportex/products");
  const tP1 = performance.now();
  const [ordersList, weeklyRaw, pricesRaw, productsRaw] = await Promise.all([
    fetchOrdersList(rememberme, centerId, year, month, 100, signal),
    fetchWeeklyOrders(rememberme, centerId, year, month, signal),
    fetchPrices(rememberme, centerId, year, month, signal),
    fetchProducts(rememberme, centerId, signal),
  ]);
  log("ok", `4 endpoints completados en ${Math.round(performance.now() - tP1)} ms · órdenes=${ordersList.length}`);

  const currency = pricesRaw?.currency || weeklyRaw?.currency || "EUR";
  const centerDescrFromApi =
    weeklyRaw?.listOrderCenters?.[0]?.descr ||
    pricesRaw?.listOrderCenters?.[0]?.descr ||
    opts.centerName || `Centro ${centerId}`;

  // Phase 2: KPIs from orders list
  const pendingReceiveTotal = ordersList.reduce((a, o) => a + (Number(o.pendingReceive) || 0), 0);
  const pendingOrderIds = new Set<string>(
    ordersList.filter((o) => (Number(o.pendingReceive) || 0) > 0).map((o) => String(o.id)),
  );
  const allOrderIds: string[] = ordersList.map((o) => String(o.id));

  // Phase 3: order details (parallel batches of 8)
  const details: Record<string, any> = {};
  if (allOrderIds.length) {
    log("call", `GET /recipes/api/order/{id}/vendor/none × ${allOrderIds.length}`);
    const tP3 = performance.now();
    const BATCH = 8;
    let done = 0;
    for (let i = 0; i < allOrderIds.length; i += BATCH) {
      const slice = allOrderIds.slice(i, i + BATCH);
      const results = await Promise.allSettled(
        slice.map((id) => fetchOrderDetail(rememberme, centerId, id, signal)),
      );
      results.forEach((r, idx) => {
        if (r.status === "fulfilled") details[slice[idx]] = r.value;
      });
      done += slice.length;
      log("info", `  detalles ${done}/${allOrderIds.length}`);
    }
    log("ok", `detalles completados en ${Math.round(performance.now() - tP3)} ms`);
  }

  const { invoice_count, pending_orders, incidents } = processOrderDetails(
    details,
    pendingOrderIds,
    { year, month },
  );

  let created_items: CreatedItem[] = [];
  try {
    log("call", "GET transactionlogs/search · usuario de soporte · productos y herramientas creados");
    const supportItems = await fetchSupportCreatedItems(rememberme, { year, month }, signal);
    created_items = supportItems.items;
    log("ok", `Registro de Cambios: ${created_items.length} productos/herramientas creados por ${supportItems.username}`);
  } catch (err: any) {
    log("err", `Registro de Cambios no disponible; el informe continúa sin esta sección: ${err?.message || err}`);
  }

  // Phase 4: analysis parsers
  const weekly_buckets = parseWeeklyBuckets(weeklyRaw);
  const price_variations = parsePriceVariations(pricesRaw);
  const new_vendors = parseNewVendors(productsRaw);
  const new_products = parseNewProducts(productsRaw);
  const new_articles = parseNewArticles(productsRaw);

  const total_received = round2(weekly_buckets.reduce((a, b) => a + b.received, 0));
  const total_not_received = round2(weekly_buckets.reduce((a, b) => a + b.not_received, 0));
  const total_purchased = round2(total_received + total_not_received);

  const recommendations: string[] = [];
  if (pending_orders.length) recommendations.push("Realizar Pedidos desde tSpoonLab");
  if (new_products.some((p) => p.type === 0)) recommendations.push("Hay productos nuevos no utilizados en Escandallos");

  log("ok", `KPIs · albaranes=${invoice_count} · pendientes=${pending_orders.length} · incidencias=${incidents.length}`);

  const monthLabel = `${MONTH_NAMES_ES[month]}/${String(year).slice(2)}`;
  return {
    report_type: "packia",
    center: centerDescrFromApi,
    center_id: centerId,
    currency,
    year, month,
    month_label: monthLabel,
    center_logo_url: buildCenterLogoUrl(rememberme, logoPath, publicOrderId),
    invoice_count,
    pending_orders_count: pending_orders.length,
    incidents_count: incidents.length,
    pending_orders,
    incidents,
    new_vendors,
    new_products,
    created_items,
    new_articles,
    weekly_buckets,
    total_received,
    total_not_received,
    total_purchased,
    price_variations,
    summary: "",
    recommendations,
  };
}
